import { inject, Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiResponse, Product } from '@shared/models';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { WindowService } from '@shared/window';

@Injectable({
    providedIn: 'root',
})
export class HttpService {
    private httpClient = inject(HttpClient);
    private translateService = inject(TranslateService);
    private windowService = inject(WindowService);

    getOthersProductsInCategory(slugs: string[]): Observable<Product[]> {
        const params: HttpParams = new HttpParams().append('categories', slugs.join(',')).append('limit', 9999);

        return forkJoin([
            this.httpClient.get<ApiResponse<Product[]>>(`api/public/products`, { params }).pipe(
                map((response) => response.data),
                map((products) => {
                    return products.map((product) => product);
                }),
            ),
            this.httpClient
                .skipErrorHandler()
                .get(`api/public/products-classic/csv/${this.translateService.currentLang}`, {
                    observe: 'response',
                    responseType: 'blob',
                })
                .pipe(
                    map((response) => response.body),
                    filter((body) => body !== null),
                    mergeMap((body) => this.csvToArray(body)),
                    catchError(() => []),
                ),
        ]).pipe(
            map(([products, additionalProducts]) => {
                const additionalProductsFiltered = additionalProducts.filter((product) => {
                    const productCatogriesSlugs = product.categories?.map((category) => category.slug);
                    return productCatogriesSlugs?.some((slug) => slugs.includes(slug));
                });
                return [...products, ...additionalProductsFiltered];
            }),
        );
    }

    csvToArray(csvFile: Blob): Observable<Product[]> {
        if (this.windowService.isBrowser) {
            const fileReader = new FileReader();
            fileReader.readAsText(csvFile);

            return new Observable((observer): void => {
                fileReader.onload = (e) => {
                    const csvString = <string>e.target?.result;
                    const response = csvString.split('\n').map(function (line) {
                        return line.split(';');
                    });
                    response.shift();
                    const products = response
                        .filter((response) => response.length === 6)
                        .map(([category, stelmachId, width, height, weight, imageUrl]) => ({
                            id: stelmachId,
                            slug: stelmachId,
                            name: stelmachId,
                            description: '',
                            patternName: stelmachId,
                            image: {
                                url: imageUrl,
                            },
                            categories: [
                                {
                                    id: category,
                                    name: category,
                                    slug: category,
                                    description: '',
                                    featured: false,
                                    image: {
                                        url: imageUrl,
                                    },
                                },
                            ],
                            parameters: {
                                height: height?.slice(0, -2) || '',
                                height_z_kam: height?.slice(0, -2) || '',
                                width: width?.slice(0, -2) || '',
                                width_z_kam: width?.slice(0, -2) || '',
                                stone_label: '',
                                default_ct_count: '',
                                weight: weight,
                            },
                        }));

                    observer.next(products);
                    observer.complete();
                };

                fileReader.onerror = (error) => {
                    observer.error(error);
                };
            });
        }
        return of([]);
    }
}
